import type {
  RfqHubFileRelease,
  RfqPackage,
  RfqProject,
  TimberRfqDrynessSpec,
  VendorWorkType,
} from '../types';
import { uploadLegacyQuoteFile, validateLegacyQuoteFile } from './legacyQuoteFileUtils';
import { isValidQuoteTotal } from './rfqQuoteTotal';
import { normalizeLeadTimeWeeksRange } from './rfqLeadTimeWeeks';
import {
  filterPackagesForReleaseSelection,
  PRE_PORTAL_RELEASE_ID,
  buildTimberApprovalUpdate,
  buildCncSupplierApprovalUpdate,
} from './timberRfqPrePortalVersion';
import {
  mergeTimberSupplierInviteForOrg,
  rfqProjectTimberSpeciesDryness,
  rfqProjectTimberSupplierSpecies,
} from './timberRfqSuppliers';
import { normalizeTimberRfqDrynessSpec } from './supplierTimberExport';
import { cncRfqAwardSubmissionKey, recordSupplierBillForRfqAward } from './supplierBillIngestion';

export type StaffSupplierQuoteUploadInput = {
  file: File;
  quoteTotal: number;
  species?: string;
  dryness?: TimberRfqDrynessSpec;
  leadTimeWeeksMin?: number;
  leadTimeWeeksMax?: number;
  notes?: string;
  receivedAt?: Date;
  markApproved?: boolean;
  quotedStructures?: import('../types').TimberSupplierStructureRef[];
};

export type StaffSupplierQuoteUploadDeps = {
  publishRfqPackage: (input: {
    rfqProjectId: string;
    supplierOrgId: string;
    targetSupplierOrgIds?: string[];
    dealId: string;
    dealTitle?: string;
    workType?: VendorWorkType;
    proposalVersionId?: string | null;
    proposalSnapshot?: { nodeSets: import('../types').ProposalNodeSet[] };
    label?: string;
    releaseDisplayLabel?: string;
    timberListSource?: 'proposal_live' | 'proposal_saved_version' | 'hub_csv' | 'hub_staged_release';
    hubFileReleaseId?: string;
    releaseVersionIndex?: number;
    cncPackagePayload?: import('../types').CncPackagePayload;
    legacyImport?: boolean;
    createdByUid: string;
    createdByName: string;
  }) => Promise<string[]>;
  createRfqSubmission: (
    row: Omit<import('../types').RfqSubmission, 'id' | 'createdAt'>,
    options?: { createdAt?: Date },
  ) => Promise<string>;
  updateRfqProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
};

export type StaffSupplierQuoteUploadContext = {
  project: RfqProject;
  orgId: string;
  orgName: string;
  workType: VendorWorkType;
  selectedReleaseId: string | null;
  hubReleases: RfqHubFileRelease[];
  projectPackages: RfqPackage[];
  staffUserId: string;
  staffUserName: string;
};

function packagesForOrgAndRelease(
  packages: RfqPackage[],
  projectId: string,
  orgId: string,
  selectedReleaseId: string | null,
): RfqPackage[] {
  const forProjectOrg = packages.filter(
    (p) => p.rfqProjectId === projectId && p.supplierOrgId === orgId,
  );
  return filterPackagesForReleaseSelection(forProjectOrg, selectedReleaseId);
}

function latestHubRelease(releases: RfqHubFileRelease[]): RfqHubFileRelease | undefined {
  if (!releases.length) return undefined;
  return [...releases].sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex)[0];
}

export async function resolvePackageIdForStaffQuoteUpload(
  deps: StaffSupplierQuoteUploadDeps,
  ctx: StaffSupplierQuoteUploadContext,
): Promise<string> {
  const projectId = ctx.project.id;
  if (!projectId) throw new Error('RFQ project is missing.');

  let releaseId = ctx.selectedReleaseId;
  if (!releaseId || releaseId === PRE_PORTAL_RELEASE_ID) {
    releaseId = latestHubRelease(ctx.hubReleases)?.id ?? null;
  }

  const existing = packagesForOrgAndRelease(
    ctx.projectPackages,
    projectId,
    ctx.orgId,
    releaseId,
  );
  if (existing[0]?.id) return existing[0].id;

  const release = releaseId ? ctx.hubReleases.find((r) => r.id === releaseId) : undefined;
  if (!release?.id) {
    throw new Error('Publish a release version before uploading a manual supplier quote.');
  }

  const siblingPkg = ctx.projectPackages.find(
    (p) => p.rfqProjectId === projectId && p.hubFileReleaseId === release.id,
  );

  const ids = await deps.publishRfqPackage({
    rfqProjectId: projectId,
    supplierOrgId: ctx.orgId,
    targetSupplierOrgIds: [ctx.orgId],
    dealId: ctx.project.dealId,
    dealTitle: ctx.project.dealTitle,
    workType: ctx.workType,
    hubFileReleaseId: release.id,
    releaseVersionIndex: release.releaseVersionIndex,
    label: siblingPkg?.label ?? release.displayLabel ?? release.fileName ?? `Release — ${ctx.orgName}`,
    releaseDisplayLabel: siblingPkg?.releaseDisplayLabel ?? release.displayLabel,
    timberListSource: siblingPkg?.timberListSource ?? 'hub_staged_release',
    proposalSnapshot: siblingPkg?.proposalSnapshot,
    proposalVersionId: siblingPkg?.proposalVersionId,
    cncPackagePayload: siblingPkg?.cncPackagePayload,
    createdByUid: ctx.staffUserId,
    createdByName: ctx.staffUserName,
  });
  if (!ids[0]) throw new Error('Could not create supplier package for this release.');
  return ids[0];
}

export async function createStaffSupplierQuoteSubmission(
  deps: StaffSupplierQuoteUploadDeps,
  ctx: StaffSupplierQuoteUploadContext,
  input: StaffSupplierQuoteUploadInput,
): Promise<string> {
  const projectId = ctx.project.id;
  if (!projectId) throw new Error('RFQ project is missing.');

  const fileError = validateLegacyQuoteFile(input.file);
  if (fileError) throw new Error(fileError);
  if (!isValidQuoteTotal(input.quoteTotal)) {
    throw new Error('Enter a valid quote amount.');
  }

  const leadRange =
    input.leadTimeWeeksMin != null || input.leadTimeWeeksMax != null
      ? normalizeLeadTimeWeeksRange(input.leadTimeWeeksMin, input.leadTimeWeeksMax)
      : null;
  if (
    (input.leadTimeWeeksMin != null || input.leadTimeWeeksMax != null)
    && leadRange === null
  ) {
    throw new Error('Lead time must be whole weeks, with min less than or equal to max.');
  }

  const species = input.species?.trim();
  if (ctx.workType === 'timber_rfq' && !species) {
    throw new Error('Select the species this quote covers.');
  }

  const packageId = await resolvePackageIdForStaffQuoteUpload(deps, ctx);
  const { storagePath, downloadUrl } = await uploadLegacyQuoteFile(
    ctx.orgId,
    packageId,
    input.file,
  );

  const releaseId =
    ctx.selectedReleaseId && ctx.selectedReleaseId !== PRE_PORTAL_RELEASE_ID
      ? ctx.selectedReleaseId
      : latestHubRelease(ctx.hubReleases)?.id ?? PRE_PORTAL_RELEASE_ID;
  const dryness = input.dryness
    ? normalizeTimberRfqDrynessSpec(input.dryness)
    : species
      ? rfqProjectTimberSpeciesDryness(ctx.project, ctx.orgId, species)
      : undefined;

  const submissionId = await deps.createRfqSubmission(
    {
      rfqPackageId: packageId,
      rfqProjectId: projectId,
      supplierOrgId: ctx.orgId,
      dealId: ctx.project.dealId,
      workType: ctx.workType,
      fileName: input.file.name,
      storagePath,
      downloadUrl,
      quoteTotal: input.quoteTotal,
      staffManualUpload: true,
      quotedSpecies: species,
      quotedDryness: dryness,
      quotedStructures: input.quotedStructures?.length ? input.quotedStructures : undefined,
      leadTimeWeeksMin: leadRange?.min,
      leadTimeWeeksMax: leadRange?.max,
      notes: input.notes?.trim() || undefined,
      contentType: input.file.type || 'application/pdf',
      sizeBytes: input.file.size,
      uploadedByUid: ctx.staffUserId,
      uploadedByName: ctx.staffUserName,
    },
    { createdAt: input.receivedAt ?? new Date() },
  );

  if (ctx.workType === 'timber_rfq' && species) {
    const existing = rfqProjectTimberSupplierSpecies(ctx.project, ctx.orgId);
    const merged = Array.from(new Set([...existing, species])).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
    await deps.updateRfqProject(projectId, {
      timberSupplierInvites: mergeTimberSupplierInviteForOrg(
        ctx.orgId,
        merged,
        { [species]: dryness ?? 'kiln_dried' },
        ctx.project.timberSupplierInvites,
      ),
    });
  }

  if (input.markApproved) {
    if (ctx.workType === 'cnc_quote') {
      await deps.updateRfqProject(
        projectId,
        buildCncSupplierApprovalUpdate(ctx.project, releaseId, ctx.orgId, true),
      );
    } else {
      await deps.updateRfqProject(
        projectId,
        buildTimberApprovalUpdate(ctx.project, releaseId, submissionId, true),
      );
    }

    const submissionKey =
      ctx.workType === 'cnc_quote' ? cncRfqAwardSubmissionKey(ctx.orgId) : submissionId;
    const description =
      ctx.workType === 'cnc_quote'
        ? `CNC quote — ${ctx.orgName}`
        : `Timber quote — ${species || input.file.name}`;
    await recordSupplierBillForRfqAward({
      project: ctx.project,
      category: ctx.workType === 'cnc_quote' ? 'cnc' : 'timber',
      orgId: ctx.orgId,
      orgName: ctx.orgName,
      submissionKey,
      amount: input.quoteTotal,
      description,
      byUserId: ctx.staffUserId,
      byUserName: ctx.staffUserName,
    });
  }

  if (ctx.project.status !== 'quoted' && ctx.project.status !== 'closed') {
    await deps.updateRfqProject(projectId, { status: 'quoted' });
  }

  return submissionId;
}
